import { encodeParam } from "utilities/misc";
import { get, post } from "utilities/network";

export type DeviceStatus = "active" | "review" | "restricted" | "blocked";
export type DeviceRestrictionScope =
  | "signup"
  | "login"
  | "rewards"
  | "outbound_transactions";

export type DeviceMetadata = Record<string, string | boolean | undefined>;

export type DeviceRow = {
  publicId: string;
  platform: "ios" | "android";
  status: DeviceStatus;
  restrictionScopes: DeviceRestrictionScope[];
  statusReason: string | null;
  integrityStatus: "pending" | "verified" | "unavailable" | "rejected";
  integrityLevel: string | null;
  platformRecallStatus: string;
  recallSyncPending: boolean;
  metadata: DeviceMetadata;
  lastSeenAt: string;
  lastAttestedAt: string | null;
  associatedAccountIds: number[];
};

export type DeviceSummary = Pick<
  DeviceRow,
  "publicId" | "platform" | "metadata" | "lastSeenAt"
>;

export type DeviceDetail = {
  device: DeviceRow & {
    statusExpiresAt: string | null;
    integrityReason: string | null;
    firstSeenAt: string;
  };
  associatedAccounts: Array<{
    id: number;
    userId: number;
    firstSeenAt: string;
    lastSeenAt: string;
    trustedAt: string | null;
    firstTrustedAt: string | null;
    trustExpiresAt: string | null;
    revokedAt: string | null;
    user: {
      userName?: string;
      email?: string;
      phoneNumber?: string;
      firstName?: string;
      lastName?: string;
    } | null;
  }>;
  events: Array<{
    id: number;
    type: string;
    metadata: Record<string, unknown>;
    createdAt: string;
  }>;
  loginAttempts: Array<{
    id: number;
    username: string;
    success: boolean;
    reason: string | null;
    integrity_status: string | null;
    ip: string;
    created_at: string;
  }>;
  transactions: Array<{
    id: number;
    transactionID: string;
    transactionType: string;
    amount: string;
    crypto: string | null;
    currencyType?: string;
    currency?: string;
    status: string;
    riskFlagReason: string | null;
    createdAt: string;
  }>;
};

export const httpFetchDeviceMetrics = () => get("admin/devices/metrics");

export const httpFetchDevices = (params: {
  page: number;
  limit: number;
  status?: string;
  platform?: string;
  integrityStatus?: string;
  userId?: number;
}) => {
  const query = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
  });
  if (params.status) query.set("status", params.status);
  if (params.platform) query.set("platform", params.platform);
  if (params.integrityStatus) {
    query.set("integrityStatus", params.integrityStatus);
  }
  if (params.userId) query.set("userId", String(params.userId));
  return get(`admin/devices?${query.toString()}`);
};

export const httpFetchDevice = (publicId: string) =>
  get(`admin/devices/${encodeParam(publicId)}`);

export const httpUpdateDeviceStatus = (
  publicId: string,
  input: {
    status: DeviceStatus;
    reason: string;
    restrictionScopes: DeviceRestrictionScope[];
    expiresAt: string | null;
  },
) => post(`admin/devices/${encodeParam(publicId)}/status`, input);
